import classes from './Details.module.sass'
import { ClusterInterface } from '../../../../../common/types/interfaces'
import { format } from 'date-fns'
import { legalEntityTypeToText } from '../../../../../common/utils/functions/enumToText'
import { IconButton } from '../../../../../common/components/iconButton/IconButton'
import edit from '../../../../../assets/images/icons/edit.svg'

type Props = {
  el?: ClusterInterface
  onEdit?: (el: ClusterInterface) => void
}

const getBillingStatus = (billing?: ClusterInterface['billing']) => {
  if (!billing?.paidUntil) {
    return { text: 'Не оплачен', isActive: false, daysLeft: null }
  }
  const now = new Date()
  const blockDate = new Date(billing.paidUntil)
  blockDate.setDate(blockDate.getDate() + (billing.blockingDelay || 0))
  const isBlocked = now > blockDate
  const daysLeft = Math.ceil((blockDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
  return {
    text: isBlocked
      ? 'Заблокирован'
      : `Оплачен до ${format(new Date(billing.paidUntil), 'dd.MM.yyyy')}`,
    isActive: !isBlocked,
    daysLeft: isBlocked ? 0 : daysLeft,
  }
}

export const Details = ({ el, onEdit }: Props) => {
  if (!el) return null

  const billingStatus = getBillingStatus(el.billing)

  return (
    <div className={classes.container}>
      <div className={classes.content}>
        <div className={classes.title}>детали кластера</div>

        {/* Основная информация */}
        <div className={classes.row}>
          <div className={classes.item}>
            <div className={classes.label}>Название</div>
            <div className={classes.value}>{el.name}</div>
          </div>
          <div className={classes.item}>
            <div className={classes.label}>ID</div>
            <div className={classes.value}>{el.id}</div>
          </div>
        </div>

        <div className={classes.item}>
          <div className={classes.label}>Описание</div>
          <div className={classes.value}>{el.description}</div>
        </div>

        <div className={classes.row}>
          <div className={classes.item}>
            <div className={classes.label}>Телефон</div>
            <div className={classes.value}>{el.phone_number}</div>
          </div>
          <div className={classes.item}>
            <div className={classes.label}>Тип</div>
            <div className={classes.value}>{el.type}</div>
          </div>
        </div>

        {/* Статусы */}
        <div className={classes.title}>статусы</div>
        <div className={classes.row}>
          <div className={classes.item}>
            <div className={classes.label}>Активен</div>
            <div className={classes.value}>{el.isActive ? '✅ Да' : '❌ Нет'}</div>
          </div>
          <div className={classes.item}>
            <div className={classes.label}>Есть доставка</div>
            <div className={classes.value}>{el.withDelivery ? '✅ Да' : '❌ Нет'}</div>
          </div>
        </div>

        {/* ТАРИФ */}
        <div className={classes.title}>тариф</div>
        {el.tariff ? (
          <>
            <div className={classes.row}>
              <div className={classes.item}>
                <div className={classes.label}>Название</div>
                <div className={classes.value}>{el.tariff.name}</div>
              </div>
              <div className={classes.item}>
                <div className={classes.label}>Активен</div>
                <div className={classes.value}>{el.tariff.isActive ? '✅ Да' : '❌ Нет'}</div>
              </div>
            </div>

            <div className={classes.row}>
              <div className={classes.item}>
                <div className={classes.label}>Базовая цена</div>
                <div className={classes.value}>{el.tariff.basePrice} ₽</div>
              </div>
              {el.tariff.specialPrice && (
                <div className={classes.item}>
                  <div className={classes.label}>Спец. цена</div>
                  <div className={classes.value}>{el.tariff.specialPrice} ₽</div>
                </div>
              )}
            </div>

            <div className={classes.row}>
              <div className={classes.item}>
                <div className={classes.label}>Срок действия</div>
                <div className={classes.value}>
                  {el.tariff.datesImportant && el.tariff.validFrom && el.tariff.validTo
                    ? `${format(new Date(el.tariff.validFrom), 'dd.MM.yyyy')} — ${format(new Date(el.tariff.validTo), 'dd.MM.yyyy')}`
                    : 'Бессрочно'}
                </div>
              </div>
              <div className={classes.item}>
                <div className={classes.label}>Скидки разрешены</div>
                <div className={classes.value}>{el.tariff.allowDiscounts ? '✅ Да' : '❌ Нет'}</div>
              </div>
            </div>

            {el.tariff.allowDiscounts && el.tariff.discounts && el.tariff.discounts.length > 0 && (
              <div className={classes.item}>
                <div className={classes.label}>Скидки за длительность</div>
                <div className={classes.value}>
                  {el.tariff.discounts.map((d) => (
                    <span key={d.durationMonths} className={classes.discount}>
                      {d.durationMonths} мес: -{d.discountPercent}%
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className={classes.value}>Тариф не установлен</div>
        )}

        {/* ОПЛАТА */}
        <div className={classes.title}>оплата</div>
        <div className={classes.row}>
          <div className={classes.item}>
            <div className={classes.label}>Статус</div>
            <div className={classes.value}>
              {billingStatus.isActive ? '✅' : '❌'} {billingStatus.text}
            </div>
          </div>
          {billingStatus.daysLeft !== null && (
            <div className={classes.item}>
              <div className={classes.label}>Дней до блокировки</div>
              <div className={classes.value}>{billingStatus.daysLeft}</div>
            </div>
          )}
        </div>
        {el.billing?.monthlyPrice && (
          <div className={classes.row}>
            <div className={classes.item}>
              <div className={classes.label}>Стоимость в месяц</div>
              <div className={classes.value}>{el.billing.monthlyPrice} ₽</div>
            </div>
            <div className={classes.item}>
              <div className={classes.label}>Отсрочка блокировки</div>
              <div className={classes.value}>{el.billing.blockingDelay || 0} дн.</div>
            </div>
          </div>
        )}
        {el.billing?.paidUntil && (
          <div className={classes.item}>
            <div className={classes.label}>Оплачен до</div>
            <div className={classes.value}>
              {format(new Date(el.billing.paidUntil), 'dd.MM.yyyy HH:mm')}
            </div>
          </div>
        )}

        {/* Юридические данные */}
        <div className={classes.title}>юридические данные</div>
        <div className={classes.row}>
          <div className={classes.item}>
            <div className={classes.label}>ОПФ</div>
            <div className={classes.value}>
              {el.legalEntityType !== undefined ? legalEntityTypeToText(el.legalEntityType) : '—'}
            </div>
          </div>
          <div className={classes.item}>
            <div className={classes.label}>ИНН</div>
            <div className={classes.value}>{el.legalInn || '—'}</div>
          </div>
        </div>

        <div className={classes.item}>
          <div className={classes.label}>Название организации</div>
          <div className={classes.value}>{el.legalName || '—'}</div>
        </div>

        <div className={classes.item}>
          <div className={classes.label}>Юридический адрес</div>
          <div className={classes.value}>{el.legalAddress || '—'}</div>
        </div>

        {/* Системная информация */}
        <div className={classes.title}>системная информация</div>
        <div className={classes.row}>
          {el.createdAt && (
            <div className={classes.item}>
              <div className={classes.label}>Создан</div>
              <div className={classes.value}>
                {format(new Date(el.createdAt), 'dd.MM.yyyy HH:mm')}
              </div>
            </div>
          )}
          {el.updatedAt && (
            <div className={classes.item}>
              <div className={classes.label}>Обновлен</div>
              <div className={classes.value}>
                {format(new Date(el.updatedAt), 'dd.MM.yyyy HH:mm')}
              </div>
            </div>
          )}
        </div>

        <div className={classes.item}>
          <div className={classes.label}>Токен</div>
          <div className={classes.token}>{el.token || '—'}</div>
        </div>

        <div className={classes.item}>
          <div className={classes.label}>Порт</div>
          <div className={classes.value}>{el.port || '—'}</div>
        </div>
      </div>

      {onEdit && (
        <div className={classes.button}>
          <IconButton small display onClick={() => onEdit(el)} icon={edit} alt="Редактировать" />
        </div>
      )}
    </div>
  )
}
